import TimeIcon from "@mui/icons-material/AccessTimeRounded";
import CancelIcon from "@mui/icons-material/CancelRounded";
import CheckIcon from "@mui/icons-material/CheckCircleRounded";
import CircleIcon from "@mui/icons-material/RadioButtonUncheckedRounded";
import RemoveCircleIcon from "@mui/icons-material/RemoveCircleOutlineRounded";
import { StepLabel, Typography } from "@mui/material";
import { orange } from "@mui/material/colors";
import { formatDistanceToNow, parseISO } from "date-fns";
import { convertISODateTime } from "@entrilia/shared/utilities/dateUtils";
import type { ApprovalStepState } from "../../../api/types/approvalTypes";

const NotStartedStepIcon = () => <CircleIcon color="secondary" />;
const PendingStepIcon = () => <TimeIcon sx={{ color: orange[700] }} />;
const ApprovedStepIcon = () => <CheckIcon color="success" />;
const SkippedStepIcon = () => <RemoveCircleIcon color="disabled" />;
const RejectedStepIcon = () => <CancelIcon color="error" />;

interface Props {
  step: ApprovalStepState;
  notStarted?: boolean;
}

const ApprovalStepLabel = ({ step, notStarted }: Props) => {
  if (notStarted) {
    return (
      <StepLabel slots={{ stepIcon: NotStartedStepIcon }}>
        <Typography variant="subtitle1" color="textPrimary">
          {step.stepName}
        </Typography>
      </StepLabel>
    );
  }

  switch (step.status) {
    case "Skipped": {
      return (
        <StepLabel slots={{ stepIcon: SkippedStepIcon }}>
          <Typography variant="subtitle1" color="textDisabled">
            {step.stepName}{" "}
            <Typography component="span" color="textSecondary" sx={{ pl: 0.5 }}>
              Skipped
            </Typography>
          </Typography>
        </StepLabel>
      );
    }
    case "Approved": {
      return (
        <StepLabel slots={{ stepIcon: ApprovedStepIcon }}>
          <Typography variant="subtitle1" color="textPrimary">
            {step.stepName}{" "}
            {step.lastActionAt && (
              <Typography component="span" color="textSecondary" sx={{ pl: 0.5 }}>
                Approved at {convertISODateTime(step.lastActionAt)}
              </Typography>
            )}
          </Typography>
        </StepLabel>
      );
    }
    case "Rejected": {
      return (
        <StepLabel slots={{ stepIcon: RejectedStepIcon }} error>
          <Typography variant="subtitle1" color="textPrimary">
            {step.stepName}{" "}
            {step.lastActionAt && (
              <Typography component="span" color="textSecondary" sx={{ pl: 0.5 }}>
                Rejected at {convertISODateTime(step.lastActionAt)}
              </Typography>
            )}
          </Typography>
        </StepLabel>
      );
    }
    case "Pending": {
      return (
        <StepLabel slots={{ stepIcon: PendingStepIcon }}>
          <Typography variant="subtitle1" color="textPrimary">
            {step.stepName}{" "}
            {step.startedAt && (
              <Typography component="span" color="textSecondary" sx={{ pl: 0.5 }}>
                Pending {formatDistanceToNow(parseISO(step.startedAt))}
              </Typography>
            )}
          </Typography>
        </StepLabel>
      );
    }
    default: {
      return null;
    }
  }
};

export default ApprovalStepLabel;
